import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface User {
  id: number
  email: string
  role: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('access_token')
    console.log('AuthContext: Checking token on mount:', token ? 'Token exists' : 'No token')
    
    if (token) {
      // Verify token and get user info
      console.log('AuthContext: Verifying token with /auth/me')
      api.get('/auth/me')
        .then(response => {
          console.log('AuthContext: Token valid, user:', response.data)
          setUser(response.data)
        })
        .catch((error) => {
          // Token is invalid, remove it
          console.log('AuthContext: Token invalid, clearing:', error.response?.status)
          localStorage.removeItem('access_token')
          setUser(null)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      console.log('AuthContext: No token found, setting user to null')
      setUser(null)
      setLoading(false)
    }
  }, [])

    const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password })
      const { access_token, user_id, role } = response.data

      localStorage.setItem('access_token', access_token)

      setUser({
        id: user_id,
        email,
        role
      })

      toast.success('Login successful!')
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('access_token')
    setUser(null)
    toast.success('Logged out successfully')
  }

  const value = {
    user,
    loading,
    login,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 